import React, { useState, useEffect } from 'react';
import { HiBriefcase, HiOutlineUserCircle, HiCheckCircle, HiMapPin, HiArrowUpRight } from 'react-icons/hi2';
import Swal from 'sweetalert2';
import { getJobOpenings, saveJobApplication } from '../data/careersData';

export default function Careers() {
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', position: '', letter: '', experience: '1 Year'
  });
  const [resumeBase64, setResumeBase64] = useState('');
  const [resumeName, setResumeName] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [openings, setOpenings] = useState([]);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  useEffect(() => {
    const loadOpenings = async () => {
      const ops = await getJobOpenings();
      setOpenings(ops);
      if (ops.length > 0) {
        setFormData(prev => ({ ...prev, position: ops[0].title }));
      }
    };
    loadOpenings();
  }, []);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate PDF format
      if (file.type !== 'application/pdf') {
        Swal.fire({
          title: 'Invalid Format',
          text: 'Only PDF documents are accepted.',
          icon: 'error',
          confirmButtonColor: '#0F5A3A'
        });
        e.target.value = '';
        setResumeBase64('');
        setResumeName('');
        return;
      }

      // Check size limit: 800 KB
      if (file.size > 800 * 1024) {
        Swal.fire({
          title: 'File Too Large',
          text: 'Your resume file size must be less than 800 KB.',
          icon: 'error',
          confirmButtonColor: '#0F5A3A'
        });
        e.target.value = '';
        setResumeBase64('');
        setResumeName('');
      } else {
        setResumeName(file.name);
        const reader = new FileReader();
        reader.onload = (evt) => {
          setResumeBase64(evt.target.result);
        };
        reader.readAsDataURL(file);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!resumeBase64) {
      Swal.fire({
        title: 'Resume Required',
        text: 'Please upload your resume in PDF format.',
        icon: 'warning',
        confirmButtonColor: '#0F5A3A'
      });
      return;
    }

    setIsSubmitted(true);
    const newApp = {
      id: `app-${Date.now()}`,
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      position: formData.position,
      experience: formData.experience,
      letter: formData.letter,
      resume: resumeBase64,
      resumeName: resumeName,
      status: 'pending',
      date: new Date().toISOString().replace('T', ' ').slice(0, 16)
    };

    try {
      await saveJobApplication(newApp);
      Swal.fire({
        title: 'Application Submitted!',
        text: `Thank you, ${formData.name}. Your application for the ${formData.position} role has been submitted successfully along with your resume.`,
        icon: 'success',
        confirmButtonColor: '#0F5A3A'
      });
      setFormData({ name: '', email: '', phone: '', position: openings[0]?.title || '', letter: '', experience: '1 Year' });
      setResumeBase64('');
      setResumeName('');
      e.target.reset();
    } catch (err) {
      Swal.fire({
        title: 'Submission Error',
        text: 'Failed to submit application. Please try again.',
        icon: 'error'
      });
    } finally {
      setIsSubmitted(false);
    }
  };

  const handleApplyClick = (title) => {
    setFormData(prev => ({ ...prev, position: title }));
    const formElement = document.getElementById('apply-form');
    if (formElement) {
      formElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const nameInput = document.getElementById('applicant-name');
      if (nameInput) nameInput.focus();
    }
  };

  const perks = [
    { title: 'Continuous OEM Training', desc: 'Regular training loops directly inside Cummins & Kirloskar regional academies.' },
    { title: 'Premium Medical Cover', desc: 'Comprehensive health insurance coverage protecting our engineers and their families.' },
    { title: 'Safety Gear & Protocols', desc: 'Standardized electrical insulation protection, acoustic ear defenders, and safety footwear.' },
    { title: 'Performance Incentives', desc: 'Monthly bonuses linked to cluster SLA compliance achievements and client reviews.' },
  ];

  return (
    <div className="page-enter bg-light font-sans pt-16">

      {/* Hero */}
      <section
        className="relative text-white py-28 overflow-hidden bg-cover bg-center"
        style={{ backgroundImage: `linear-gradient(105deg, rgba(0, 0, 0, 0.9) 0%, rgba(0, 0, 0, 0.65) 50%, rgba(0, 0, 0, 0.3) 100%), url('/Careers.png')` }}
      >
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-accent/30 to-transparent" />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 relative z-20">
          <span className="eyebrow">Join Our Team</span>
          <h1 className="text-4xl md:text-6xl font-display font-extrabold mb-5 leading-tight">
            Careers at <span className="text-gradient-gold">Green Gusto</span>
          </h1>
          <p className="text-base text-white/65 max-w-2xl leading-relaxed">
            Build a premium career in energy engineering, telemetry NOC operations, and massive solar grid EPC infrastructure.
          </p>
        </div>
      </section>

      {/* Breadcrumb */}
      <div className="bg-white border-b border-border py-3.5">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center gap-2 text-xs text-body-muted">
          <span>Home</span>
          <span className="text-border-dark">/</span>
          <span className="font-semibold text-primary">Careers</span>
        </div>
      </div>

      {/* Work Culture / Benefits */}
      <section className="py-24 max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <span className="eyebrow">Work Culture</span>
          <h2 className="text-3xl md:text-4xl font-display font-extrabold text-heading mb-5">
            Pioneering Safety, Growth, and OEM Technical Qualifications
          </h2>
          <p className="text-sm text-body leading-relaxed">
            At Green Gusto Energy, we believe our engineering strength lies entirely in the expertise of our workforce. We do not hire random sub-contractors; we invest heavily in training, insurance benefits, and official OEM alignment.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {perks.map((perk, i) => (
            <div key={i} className="bg-white border border-border rounded-2xl p-6 shadow-card hover:shadow-card-hover hover:-translate-y-1 transition-all duration-300 group">
              <HiCheckCircle className="w-6 h-6 text-accent mb-4" />
              <h4 className="text-xs font-bold text-heading uppercase tracking-wide mb-2">{perk.title}</h4>
              <p className="text-xs text-body leading-relaxed">{perk.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Open Positions + Application Form */}
      <section className="py-20 bg-light-section border-t border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">

          {/* Job Listings */}
          <div className="lg:col-span-7 space-y-6">
            <div>
              <span className="eyebrow">Active Listings</span>
              <h2 className="text-2xl md:text-3xl font-display font-extrabold text-heading">Current Openings</h2>
            </div>

            {openings.map((job, idx) => (
              <div key={job.id || idx} className="bg-white border border-border rounded-2xl p-8 shadow-card hover:shadow-card-hover transition-all duration-300">
                <div className="flex items-center justify-between mb-5">
                  <span className="badge-gold text-[9px]">{job.dept}</span>
                  <span className="px-3 py-1 border border-border text-body-muted text-[9px] font-bold rounded-full uppercase tracking-wider">Full-Time</span>
                </div>
                <h3 className="text-lg font-display font-extrabold text-heading mb-3">{job.title}</h3>
                <hr className="border-border my-4" />
                <div className="flex flex-wrap gap-x-6 gap-y-2 text-xs text-body mb-4">
                  <div className="flex items-center gap-1.5">
                    <HiMapPin className="text-accent w-4 h-4" />
                    <span>Location: <strong className="text-heading">{job.location}</strong></span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <HiBriefcase className="text-accent w-4 h-4" />
                    <span>Experience: <strong className="text-heading">{job.experience}</strong></span>
                  </div>
                </div>
                <p className="text-sm text-body leading-relaxed mb-5">{job.desc}</p>
                {job.requirements && Array.isArray(job.requirements) && job.requirements.length > 0 && (
                  <div className="bg-light-section border border-border rounded-xl p-5 mb-5">
                    <h4 className="text-[10px] font-bold text-heading uppercase tracking-wider mb-3">Key Requirements:</h4>
                    <ul className="space-y-2">
                      {job.requirements.map((req, ri) => (
                        <li key={ri} className="flex items-start gap-2 text-xs text-body">
                          <span className="text-accent font-extrabold text-sm mt-0.5">›</span>
                          {req}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <button
                  onClick={() => handleApplyClick(job.title)}
                  className="btn-accent py-2.5 px-6 rounded-xl text-xs"
                >
                  Apply For This Role
                  <HiArrowUpRight className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>

          {/* Application Form */}
          <div id="apply-form" className="lg:col-span-5 bg-charcoal text-white border border-white/5 rounded-3xl p-8 shadow-premium lg:sticky lg:top-24 relative overflow-hidden">
            <div className="absolute -top-10 -right-10 w-36 h-36 bg-accent/8 rounded-full blur-2xl" />
            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-2">
                <HiOutlineUserCircle className="w-5 h-5 text-accent" />
                <span className="text-xs font-bold text-accent uppercase tracking-widest">Apply For a Position</span>
              </div>
              <h3 className="text-xl font-display font-extrabold text-white mb-2">Submit Your Application</h3>
              <p className="text-xs text-white/60 mb-8">Complete the details, attach your resume, and our team will review it promptly.</p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-[10px] text-white/50 mb-1.5 font-bold uppercase tracking-widest">Full Name</label>
                  <input id="applicant-name" type="text" name="name" value={formData.name} onChange={handleChange} required placeholder="Enter your name" className="input-dark" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[10px] text-white/50 mb-1.5 font-bold uppercase tracking-widest">Email Address</label>
                    <input type="email" name="email" value={formData.email} onChange={handleChange} required placeholder="name@email.com" className="input-dark" />
                  </div>
                  <div>
                    <label className="block text-[10px] text-white/50 mb-1.5 font-bold uppercase tracking-widest">Mobile Number</label>
                    <input type="tel" name="phone" value={formData.phone} onChange={handleChange} required placeholder="+91 96096 00600" className="input-dark" />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[10px] text-white/50 mb-1.5 font-bold uppercase tracking-widest">Select Position</label>
                    <select name="position" value={formData.position} onChange={handleChange} className="input-dark bg-charcoal text-white/85 w-full outline-none">
                      {openings.map(op => (
                        <option key={op.id} value={op.title}>{op.title} ({op.location})</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-[10px] text-white/50 mb-1.5 font-bold uppercase tracking-widest">Years of Experience</label>
                    <input type="text" name="experience" value={formData.experience} onChange={handleChange} required placeholder="e.g. 5 Years" className="input-dark" />
                  </div>
                </div>
                <div>
                  <label className="block text-[10px] text-white/50 mb-1.5 font-bold uppercase tracking-widest">Upload Resume (PDF ONLY – Max 800KB)</label>
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={handleFileChange}
                    required
                    className="w-full px-4 py-2.5 rounded-xl border border-white/10 text-white/70 text-xs focus:outline-none focus:border-primary-light focus:ring-2 focus:ring-primary-light/20 transition-all file:mr-4 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-white/10 file:text-white hover:file:bg-white/20 file:cursor-pointer bg-white/8 backdrop-blur-sm"
                  />
                </div>
                <div>
                  <label className="block text-[10px] text-white/50 mb-1.5 font-bold uppercase tracking-widest">Cover Letter Summary</label>
                  <textarea name="letter" value={formData.letter} onChange={handleChange} rows={4} required placeholder="Introduce yourself, your experience and OEM credentials..." className="input-dark resize-none" />
                </div>
                <button type="submit" disabled={isSubmitted} className="btn-green rounded-xl w-full justify-center py-3.5 text-sm disabled:opacity-60">
                  {isSubmitted ? 'Submitting...' : 'Submit Application'}
                  <HiArrowUpRight className="w-4 h-4" />
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
